import { useEffect, useState } from 'react'

/**
 * What a licence NFT carries in unclaimed distribution, read from the platform
 * rather than from whoever is selling it.
 *
 * A licence takes its unclaimed distribution with it, and since the historical
 * register that is tens of thousands of MPRO on a typical licence — often more
 * than the asking price. A seller can claim any figure; this is the figure the
 * payout will actually use, and it needs no wallet to read, so a buyer can
 * check it before committing to anything.
 */
const BASE =
  (import.meta.env.VITE_INDEX_NODE_SERVICE_API_URL as string | undefined) ??
  '/api/index-node-licenses/'

export interface UnclaimedForToken {
  loading: boolean
  /** MPRO the next owner of this token can mint. Null while unknown. */
  unclaimedMpro: number | null
}

export function useUnclaimedForToken(
  nftContract: string | undefined,
  tokenId: string | undefined
): UnclaimedForToken {
  const [state, setState] = useState<UnclaimedForToken>({
    loading: Boolean(nftContract && tokenId),
    unclaimedMpro: null
  })

  useEffect(() => {
    if (!nftContract || !tokenId) {
      setState({ loading: false, unclaimedMpro: null })
      return undefined
    }
    let cancelled = false
    setState({ loading: true, unclaimedMpro: null })
    const url = `${BASE.replace(/\/$/, '')}/licenses/unclaimed/${nftContract.toLowerCase()}/${tokenId}`
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return
        // An unreadable figure stays null: showing zero would be a claim we
        // cannot support, and this number talks people into transactions.
        const value =
          data && typeof data.unclaimedMpro === 'number' ? data.unclaimedMpro : null
        setState({ loading: false, unclaimedMpro: value })
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, unclaimedMpro: null })
      })
    return () => {
      cancelled = true
    }
  }, [nftContract, tokenId])

  return state
}
